import { useMemo, useState } from 'react';
import type { Campaign, CampaignPreview, CommsChannel } from '@salonomy/types';
import { Button, Card, Field, Pill, Select, TextArea, TextInput } from '@salonomy/ui';
import { useCreateCampaign, usePreviewCampaign, useSendCampaign } from '../../data/comms.js';
import { ApiClientError } from '../../lib/api.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';

/**
 * WS8 — the campaign composer. The flow is deliberately three-step and the PREVIEW is the bulk-spend
 * safety gate: you compose, you Create a draft, the server resolves the audience and returns a dry-run
 * (recipientCount / estimatedCostPence / wouldPauseAtCap), and ONLY THEN does Launch unlock. We never
 * compute cost on the client — every figure shown is the server's estimate. Launch is manager-only,
 * gated on isPending, and idempotent on a key minted once per draft.
 */

const CHANNELS: Array<{ value: CommsChannel; label: string }> = [
  { value: 'email', label: 'Email (always free)' },
  { value: 'push', label: 'App push (always free)' },
  { value: 'sms', label: 'SMS (metered)' },
  { value: 'whatsapp', label: 'WhatsApp (metered)' },
];

function newKey(): string {
  return crypto.randomUUID();
}

export function CampaignComposer({ canManage }: { canManage: boolean }) {
  const toast = useToast();
  const createCampaign = useCreateCampaign();
  const previewCampaign = usePreviewCampaign();
  const sendCampaign = useSendCampaign();

  // Form state
  const [name, setName] = useState('');
  const [channel, setChannel] = useState<CommsChannel>('email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [notSeenForDays, setNotSeenForDays] = useState('');
  const [minVisitCount, setMinVisitCount] = useState('');

  // The draft + its preview. A new draft clears the preview so Launch can never fire against a stale
  // estimate. The idempotency keys are minted once per intent.
  const [draft, setDraft] = useState<Campaign | null>(null);
  const [preview, setPreview] = useState<CampaignPreview | null>(null);
  const [createKey, setCreateKey] = useState(newKey);
  // sendKey is sent on Launch but the server's launch idempotency is per-campaign (status guard +
  // campaign:<id> job de-dupe), NOT key-based — see data/comms.ts. We still re-mint it per launch so a
  // second campaign never reuses a stale key once the route starts honouring it.
  const [sendKey, setSendKey] = useState(newKey);

  const isEmail = channel === 'email';
  const canCreate = canManage && name.trim().length > 0 && body.trim().length > 0;

  // Any edit to the composed content invalidates a previously-created draft/preview: you must Create +
  // Preview again so Launch always reflects exactly what the server costed.
  function resetDraft() {
    if (draft || preview) {
      setDraft(null);
      setPreview(null);
      setCreateKey(newKey());
    }
  }

  const audienceFilter = useMemo(() => {
    const f: { notSeenForDays?: number; minVisitCount?: number } = {};
    const nsd = Number.parseInt(notSeenForDays, 10);
    const mvc = Number.parseInt(minVisitCount, 10);
    if (Number.isFinite(nsd) && nsd > 0) f.notSeenForDays = nsd;
    if (Number.isFinite(mvc) && mvc > 0) f.minVisitCount = mvc;
    return f;
  }, [notSeenForDays, minVisitCount]);

  async function doCreateAndPreview() {
    try {
      const created = await createCampaign.mutateAsync({
        name: name.trim(),
        channel,
        subject: isEmail && subject.trim() ? subject.trim() : null,
        body: body.trim(),
        audienceFilter,
        // Marketing campaigns require opt-in consent; the server enforces it and the preview shows the
        // consent skips. We never silently message clients who haven't opted in.
        requiresMarketingConsent: true,
        idempotencyKey: createKey,
      });
      setDraft(created);
      // Immediately dry-run so the manager sees the cost/recipient impact before any Launch is possible.
      const pv = await previewCampaign.mutateAsync(created.id);
      setPreview(pv);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not preview the campaign');
    }
  }

  async function doLaunch() {
    if (!draft || !preview) return;
    try {
      await sendCampaign.mutateAsync({ id: draft.id, idempotencyKey: sendKey });
      toast.success(`Launching “${draft.name}” to ${preview.recipientCount} recipients`);
      // Reset the composer for the next campaign.
      setDraft(null);
      setPreview(null);
      setName('');
      setSubject('');
      setBody('');
      setNotSeenForDays('');
      setMinVisitCount('');
      setCreateKey(newKey());
      setSendKey(newKey());
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not launch the campaign');
    }
  }

  if (!canManage) {
    return (
      <Card>
        <div className="section-title">Campaign composer</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
          Campaigns reach a lot of clients and can cost money on paid channels, so only an owner or
          manager can compose and launch them. Ask a manager to run a campaign.
        </div>
      </Card>
    );
  }

  const previewing = createCampaign.isPending || previewCampaign.isPending;

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="section-title">Campaign composer</div>
        <Pill tone="soft">Preview before you send</Pill>
      </div>
      <div
        className="muted"
        style={{ fontSize: 13, marginTop: 2, marginBottom: 14, lineHeight: 1.5 }}
      >
        Compose, then preview the exact reach and cost — we resolve the audience, skip anyone
        without consent or a contact, and show what it would cost before you launch. We never
        auto-spend past your cap.
      </div>

      <div className="comms-composer">
        <Field label="Campaign name">
          <TextInput
            value={name}
            placeholder="e.g. We miss you — book this month"
            onChange={(e) => {
              setName(e.target.value);
              resetDraft();
            }}
          />
        </Field>

        <div className="comms-composer__row">
          <Field
            label="Channel"
            hint="Email & push are always free; SMS/WhatsApp draw your allowance."
          >
            <Select
              value={channel}
              onChange={(e) => {
                setChannel(e.target.value as CommsChannel);
                resetDraft();
              }}
            >
              {CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
          </Field>
          {isEmail ? (
            <Field label="Subject" hint="Email only">
              <TextInput
                value={subject}
                maxLength={200}
                placeholder="Subject line"
                onChange={(e) => {
                  setSubject(e.target.value);
                  resetDraft();
                }}
              />
            </Field>
          ) : (
            <div />
          )}
        </div>

        <Field label="Message" hint={`${body.length}/4000`}>
          <TextArea
            value={body}
            rows={5}
            maxLength={4000}
            placeholder="Write your message. Personalisation tokens from your template are supported."
            onChange={(e) => {
              setBody(e.target.value);
              resetDraft();
            }}
          />
        </Field>

        <div className="comms-audience">
          <div className="comms-audience__title">Who gets this</div>
          <div className="comms-composer__row">
            <Field label="Not seen for (days)" hint="Leave blank for everyone">
              <TextInput
                type="number"
                min={0}
                value={notSeenForDays}
                placeholder="e.g. 90"
                onChange={(e) => {
                  setNotSeenForDays(e.target.value);
                  resetDraft();
                }}
              />
            </Field>
            <Field label="Min visits">
              <TextInput
                type="number"
                min={0}
                value={minVisitCount}
                placeholder="e.g. 2"
                onChange={(e) => {
                  setMinVisitCount(e.target.value);
                  resetDraft();
                }}
              />
            </Field>
          </div>
          <div className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
            Marketing-consent and contactability are enforced by the server — anyone who hasn’t
            opted in, or has no reachable contact, is skipped and shown in the preview.
          </div>
        </div>

        {/* Step 1: Create the draft + run the dry-run preview. */}
        {!preview ? (
          <div>
            <Button
              variant="primary"
              onClick={doCreateAndPreview}
              disabled={!canCreate || previewing}
            >
              {previewing ? 'Resolving audience…' : 'Preview reach & cost'}
            </Button>
          </div>
        ) : (
          <PreviewPanel
            preview={preview}
            channel={channel}
            launching={sendCampaign.isPending}
            onLaunch={doLaunch}
            onEdit={resetDraft}
          />
        )}
      </div>
    </Card>
  );
}

/**
 * The dry-run result — the bulk-spend safety surface. Renders the server's recipientCount /
 * estimatedCostPence / wouldPauseAtCap (never a client-side cost), plus the consent/contact skips, and
 * gates the Launch button on the preview existing. wouldPauseAtCap is surfaced loudly: the server won't
 * auto-spend past the cap, so the manager knows the campaign will pause rather than overspend.
 */
function PreviewPanel({
  preview,
  channel,
  launching,
  onLaunch,
  onEdit,
}: {
  preview: CampaignPreview;
  channel: CommsChannel;
  launching: boolean;
  onLaunch: () => void;
  onEdit: () => void;
}) {
  const metered = channel === 'sms' || channel === 'whatsapp';
  return (
    <div className={`comms-preview${preview.wouldPauseAtCap ? ' comms-preview--cap' : ''}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>This is what your campaign will do</div>
        {preview.wouldPauseAtCap ? (
          <Pill tone="danger">Would pause at cap</Pill>
        ) : metered ? (
          <Pill tone="cobalt">Metered channel</Pill>
        ) : (
          <Pill tone="success">Free channel</Pill>
        )}
      </div>

      <div className="comms-preview__stats">
        <Stat label="Recipients" value={String(preview.recipientCount)} />
        {/* Always the SERVER's estimatedCostPence — never computed here. */}
        <Stat
          label="Estimated cost"
          value={preview.estimatedCostPence > 0 ? money(preview.estimatedCostPence) : 'Free'}
        />
        <Stat label="Est. segments" value={String(preview.estimatedSegments)} />
      </div>

      {preview.consentSkippedCount > 0 || preview.noContactSkippedCount > 0 ? (
        <div className="comms-preview__skips">
          We’ll skip{' '}
          {preview.consentSkippedCount > 0 ? (
            <strong>{preview.consentSkippedCount} without marketing consent</strong>
          ) : null}
          {preview.consentSkippedCount > 0 && preview.noContactSkippedCount > 0 ? ' and ' : ''}
          {preview.noContactSkippedCount > 0 ? (
            <strong>{preview.noContactSkippedCount} with no reachable contact</strong>
          ) : null}
          . Only opted-in, contactable clients are messaged.
        </div>
      ) : (
        <div className="comms-preview__skips">
          Everyone in this audience is opted in and contactable — no one is skipped.
        </div>
      )}

      {preview.wouldPauseAtCap ? (
        <div className="comms-preview__skips">
          This run would hit your paid-message cap. We <strong>won’t</strong> auto-spend past it —
          the campaign pauses at the cap and follows your at-cap policy above.
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {/* Launch is only ever offered AFTER a preview exists (this panel only renders with a preview). */}
        <Button variant="primary" onClick={onLaunch} disabled={launching}>
          {launching
            ? 'Launching…'
            : preview.recipientCount === 0
              ? 'No recipients to send to'
              : `Launch to ${preview.recipientCount} ${preview.recipientCount === 1 ? 'recipient' : 'recipients'}`}
        </Button>
        <Button variant="outline" onClick={onEdit} disabled={launching}>
          Edit campaign
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        className="muted"
        style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.4 }}
      >
        {label}
      </div>
      <div style={{ fontFamily: 'var(--slm-font-display)', fontSize: 22, marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}
